import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../../../../cartographie/models/cartographie.model';
import { ButtonAddNewComponent } from '../../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { Aptitude } from '../../../processus-aptitude/models/aptitude.model';
import { Phase } from '../../../processus-phases/models/phase.model';
import { PhaseStore } from '../../../processus-phases/store/phase.store';
import { IndiceRésultats } from '../../models/indice-résultats';
import { IndiceCreateRequest } from '../../requests/create-request';
import { IndiceRésultatsStore } from '../../store/indice-résultats.store';
import { FormItemIndiceComponent } from './form-item-indice/form-item-indice.component';
import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-indices',
  imports: [ReactiveFormsModule, ButtonAddNewComponent, FormItemIndiceComponent],
  templateUrl: './form-indices.component.html',
})
export class FormIndicesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #indiceStore = inject(IndiceRésultatsStore);
  readonly #phaseStore = inject(PhaseStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);

  readonly phases = input.required<Phase[]>();
  readonly indiceResultat = input<IndiceRésultats>();
  readonly referentiel = input.required<Référentiel>();
  readonly cartographie = input.required<Cartographie>();
  readonly estCreation = input.required<boolean>();
  readonly aptitude = input.required<Aptitude>();

  readonly form = this.#fb.group({
    indices: this.#fb.array([]),
  });

  constructor() {
    effect(() => {
      if (!this.estCreation()) {
        this.aptitude().indicesResultats.forEach((indiceResultats) => {
          this.addIndice(indiceResultats);
        });
      } else {
        this.addIndice();
      }
      this.#indiceStore.charger(this.referentiel().id);
    });
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addIndice(indice?: IndiceRésultats) {
    const form = this.#fb.group({
      id: [indice?.id],
      nom: [indice?.nom, [Validators.required]],
      nomAptitude: [''],
    });
    this.indicesForm.push(form);
  }

  onSubmit() {
    const toastrId = uuidv7();
    if (this.estCreation()) {
      const nouveauxIndices: IndiceCreateRequest = {
        idReferentiel: this.referentiel().id,
        idAptitude: this.aptitude().id,
        indices: this.indicesForm.value
          .filter((x: { nom: string }) => !!x.nom?.trim())
          .map((x: { nom: string; aptitude: string }) => x.nom),
      };

      if (
        !nouveauxIndices.indices ||
        nouveauxIndices.indices.length === 0 ||
        this.alreadyExists(nouveauxIndices.indices.map((x) => ({ nom: x })))
      ) {
        this.form.markAllAsTouched();
        return;
      }

      this.#indiceStore.créer({
        data: nouveauxIndices,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: 'Création des indices de résultats réussie',
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.#phaseStore.charger(this.referentiel().id);
        },
        error: () => {},
      });
    }
  }

  alreadyExists(
    indices: {
      nom: string;
      id?: string;
    }[],
  ) {
    const idAptitude = this.aptitude().id;

    let doublonsPrésents = false;
    const tousLesIndices = this.#indiceStore.entities().map((x) => ({
      nom: x.nom,
      idAptitude: x.idAptitude,
      id: x.id,
    }));

    const doublonsAvecIndicesExistants = tousLesIndices.filter(
      (x) =>
        indices.some((y) => idAptitude === x.idAptitude && y.nom === x.nom) &&
        // pour ne pas compter ceux que nous n'avons pas modifié dans le sidepanel
        !indices.some((y) => idAptitude === x.idAptitude && y.nom === x.nom && y.id === x.id),
    );

    this.indicesForm.controls.forEach((control) => {
      if (
        doublonsAvecIndicesExistants.some((x) => x.nom === control.value.nom) ||
        this.indicesForm.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
        doublonsPrésents = true;
      }
    });

    return doublonsPrésents;
  }

  get indicesForm() {
    return this.form.get('indices') as FormArray<FormGroup>;
  }
}
